import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Layers, Wand2, FileDown } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import {
  createEmployee,
  deleteEmployee,
  getEmployeeComponents,
  getEmployees,
  getPayComponents,
  assignComponentToEmployee,
  updateEmployee,
  getPayrollJobGrades,
  applyGradeTemplateToEmployee,
} from "../payrollService";
import PayrollEmployeeForm from "./PayrollEmployeeForm";
import PayrollTableEmpty from "../components/PayrollTableEmpty";
import { exportPayrollEmployeesPDF } from "../components/PayrollEmployeesPDF";
import { exportPayrollEmployeesExcel } from "../components/PayrollEmployeesExcel";

function pickOne(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

export default function PayrollEmployees() {
  const { companyId, company } = useCompany();
  const [rows, setRows] = useState([]);
  const [components, setComponents] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);

  const [assignOpenFor, setAssignOpenFor] = useState(null);
  const [employeeComponents, setEmployeeComponents] = useState([]);
  const [assignForm, setAssignForm] = useState({
    component_id: "",
    amount: "",
    percentage: "",
    effective_from: "",
  });
  const [grades, setGrades] = useState([]);
  const [showApplyGradeModal, setShowApplyGradeModal] = useState(false);
  const [selectedEmployeeForGrade, setSelectedEmployeeForGrade] =
    useState(null);
  const [applyingGrade, setApplyingGrade] = useState(false);

  const [applyGradeForm, setApplyGradeForm] = useState({
    grade_id: "",
    mode: "replace",
  });

  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      const [employeesData, componentsData, gradeData] = await Promise.all([
        getEmployees(companyId),
        getPayComponents(companyId),
        getPayrollJobGrades(companyId),
      ]);

      setRows(employeesData || []);
      setComponents(componentsData || []);
      setGrades(gradeData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load employees");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  function openApplyGradeModal(employee) {
    setSelectedEmployeeForGrade(employee);
    setApplyGradeForm({
      grade_id: employee.job_grade_id || "",
      mode: "replace",
    });
    setShowApplyGradeModal(true);
  }

  async function handleApplyGradeToEmployee(e) {
    e.preventDefault();

    if (!selectedEmployeeForGrade?.id) {
      setMsg("No employee selected.");
      return;
    }

    try {
      setApplyingGrade(true);
      setMsg("");

      await applyGradeTemplateToEmployee({
        companyId,
        employeeId: selectedEmployeeForGrade.id,
        gradeId: applyGradeForm.grade_id,
        mode: applyGradeForm.mode,
      });

      setShowApplyGradeModal(false);
      setSelectedEmployeeForGrade(null);
      setApplyGradeForm({
        grade_id: "",
        mode: "replace",
      });

      await load();
      setMsg("Grade template applied successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to apply grade template");
    } finally {
      setApplyingGrade(false);
    }
  }

  async function handleSaveEmployee(payload) {
    try {
      setMsg("");
      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, payload);
      } else {
        await createEmployee({
          ...payload,
          company_id: companyId,
        });
      }
      setFormOpen(false);
      setEditingEmployee(null);
      load();
    } catch (err) {
      setMsg(err.message || "Failed to save employee");
    }
  }

  async function handleDelete(row) {
    const ok = window.confirm(`Delete ${row.first_name} ${row.last_name}?`);
    if (!ok) return;
    try {
      await deleteEmployee(row.id);
      load();
    } catch (err) {
      setMsg(err.message || "Failed to delete employee");
    }
  }

  async function openAssignments(employee) {
    try {
      setAssignOpenFor(employee);
      setAssignForm({
        component_id: "",
        amount: "",
        percentage: "",
        effective_from: "",
      });
      const data = await getEmployeeComponents(companyId, employee.id);
      setEmployeeComponents(data);
    } catch (err) {
      setMsg(err.message || "Failed to load employee components");
    }
  }

  async function saveAssignment() {
    if (!assignOpenFor) return;
    try {
      await assignComponentToEmployee({
        company_id: companyId,
        employee_id: assignOpenFor.id,
        component_id: assignForm.component_id,
        amount: Number(assignForm.amount || 0),
        percentage: Number(assignForm.percentage || 0),
        effective_from:
          assignForm.effective_from || new Date().toISOString().slice(0, 10),
      });
      const data = await getEmployeeComponents(companyId, assignOpenFor.id);
      setEmployeeComponents(data);
      setAssignForm({
        component_id: "",
        amount: "",
        percentage: "",
        effective_from: "",
      });
    } catch (err) {
      setMsg(err.message || "Failed to assign component");
    }
  }

  function handleExportEmployeesPDF() {
    try {
      setMsg("");

      exportPayrollEmployeesPDF({
        company,
        employees: rows || [],
        title: "Payroll Employees List",
      });

      setMsg("Employees list exported successfully.");
    } catch (err) {
      console.error(err);
      setMsg(err.message || "Failed to export employees list");
    }
  }

  function handleExportEmployeesExcel() {
    try {
      setMsg("");

      exportPayrollEmployeesExcel({
        company,
        employees: rows || [],
        fileLabel: "Employees_List",
      });

      setMsg("Employees list exported to Excel successfully.");
    } catch (err) {
      console.error(err);
      setMsg(err.message || "Failed to export employees list to Excel");
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert error">{msg}</div> : null}

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Employees</h3>
            <p>Maintain payroll-ready employee master records.</p>
          </div>
          <button
            className="primary-btn"
            type="button"
            onClick={() => {
              setEditingEmployee(null);
              setFormOpen(true);
            }}
          >
            <Plus size={16} />
            Add Employee
          </button>
          <button
            type="button"
            className="secondary-btn"
            onClick={handleExportEmployeesPDF}
          >
            <FileDown size={16} />
            Export PDF
          </button>
          <button
            type="button"
            className="secondary-btn"
            onClick={handleExportEmployeesExcel}
          >
            <FileDown size={16} />
            Export Excel
          </button>
        </div>

        {!rows.length && !busy ? (
          <PayrollTableEmpty title="No employees yet" />
        ) : (
          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Job Title</th>
                  <th>Status</th>
                  <th>Base Salary</th>
                  <th>Level</th>
                  <th>Grade</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.employee_code}</td>
                    <td>
                      {row.first_name} {row.last_name}
                    </td>
                    <td>{row.department || "-"}</td>
                    <td>{row.job_title || "-"}</td>
                    <td>
                      <span className={`pill ${row.employment_status}`}>
                        {row.employment_status}
                      </span>
                    </td>
                    <td>{Number(row.base_salary || 0).toLocaleString()}</td>
                    <td>{row.job_level_name || row.job_level_code || "-"}</td>
                    <td>{row.job_grade_name || row.job_grade_code || "-"}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={() => {
                            setEditingEmployee(row);
                            setFormOpen(true);
                          }}
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          onClick={() => openAssignments(row)}
                        >
                          <Layers size={16} />
                        </button>
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={() => openApplyGradeModal(row)}
                        >
                          <Wand2 size={16} />
                          Apply Grade
                        </button>
                        <button
                          type="button"
                          className="icon-btn danger"
                          onClick={() => handleDelete(row)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {busy && (
                  <tr>
                    <td colSpan="7">Loading...</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PayrollEmployeeForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingEmployee(null);
        }}
        onSubmit={handleSaveEmployee}
        editingEmployee={editingEmployee}
      />

      {assignOpenFor ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>
                Employee Components — {assignOpenFor.first_name}{" "}
                {assignOpenFor.last_name}
              </h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setAssignOpenFor(null)}
              >
                Close
              </button>
            </div>

            <div className="payroll-grid payroll-grid-4 payroll-inline-form">
              <select
                value={assignForm.component_id}
                onChange={(e) =>
                  setAssignForm((prev) => ({
                    ...prev,
                    component_id: e.target.value,
                  }))
                }
              >
                <option value="">Select component</option>
                {components.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.component_name} ({c.component_type})
                  </option>
                ))}
              </select>

              <input
                type="number"
                step="0.01"
                placeholder="Amount"
                value={assignForm.amount}
                onChange={(e) =>
                  setAssignForm((prev) => ({ ...prev, amount: e.target.value }))
                }
              />

              <input
                type="number"
                step="0.0001"
                placeholder="Percentage"
                value={assignForm.percentage}
                onChange={(e) =>
                  setAssignForm((prev) => ({
                    ...prev,
                    percentage: e.target.value,
                  }))
                }
              />

              <input
                type="date"
                value={assignForm.effective_from}
                onChange={(e) =>
                  setAssignForm((prev) => ({
                    ...prev,
                    effective_from: e.target.value,
                  }))
                }
              />
            </div>

            <div className="payroll-form-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={saveAssignment}
              >
                Save Assignment
              </button>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Percentage</th>
                    <th>Effective From</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeComponents.map((row) => {
                    const component = Array.isArray(row.payroll_pay_components)
                      ? row.payroll_pay_components[0]
                      : row.payroll_pay_components;
                    return (
                      <tr key={row.id}>
                        <td>{component?.component_name || "-"}</td>
                        <td>{component?.component_type || "-"}</td>
                        <td>{Number(row.amount || 0).toLocaleString()}</td>
                        <td>{Number(row.percentage || 0).toLocaleString()}</td>
                        <td>{row.effective_from}</td>
                      </tr>
                    );
                  })}
                  {!employeeComponents.length && (
                    <tr>
                      <td colSpan="5">No assigned components yet.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
      {showApplyGradeModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <div>
                <h3>Apply Grade Template</h3>
                <p>
                  {selectedEmployeeForGrade
                    ? `Apply a level/grade framework to ${
                        selectedEmployeeForGrade.first_name || ""
                      } ${selectedEmployeeForGrade.last_name || ""}`.trim()
                    : "Apply a level/grade framework"}
                </p>
              </div>

              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  setShowApplyGradeModal(false);
                  setSelectedEmployeeForGrade(null);
                }}
              >
                Close
              </button>
            </div>

            <form
              className="payroll-form"
              onSubmit={handleApplyGradeToEmployee}
            >
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Grade
                  <select
                    value={applyGradeForm.grade_id}
                    onChange={(e) =>
                      setApplyGradeForm((p) => ({
                        ...p,
                        grade_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select grade</option>
                    {grades.map((row) => {
                      const level = pickOne(row.payroll_job_levels);
                      return (
                        <option key={row.id} value={row.id}>
                          {level?.level_name || "Level"} - {row.grade_name}
                        </option>
                      );
                    })}
                  </select>
                </label>

                <label>
                  Apply Mode
                  <select
                    value={applyGradeForm.mode}
                    onChange={(e) =>
                      setApplyGradeForm((p) => ({
                        ...p,
                        mode: e.target.value,
                      }))
                    }
                  >
                    <option value="replace">Replace employee setup</option>
                    <option value="fill_empty">Fill empty only</option>
                  </select>
                </label>
              </div>

              <div className="payroll-alert info">
                Replace mode will overwrite compensation-related setup for this
                employee. Fill empty only will only populate missing defaults.
              </div>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => {
                    setShowApplyGradeModal(false);
                    setSelectedEmployeeForGrade(null);
                  }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="primary-btn"
                  disabled={applyingGrade}
                >
                  <Wand2 size={16} />
                  {applyingGrade ? "Applying..." : "Apply Grade Template"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
